import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { renderMarkdown } from "@/lib/markdown";
import { LOCATION_PAGES } from "@/data/locationContent";

const ensureMeta = (name) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    return tag;
};

const ensureCanonical = () => {
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
    }
    return tag;
};

const upsertJsonLd = (id, data) => {
    let tag = document.getElementById(id);
    if (!tag) {
        tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.id = id;
        document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(data);
};

const upsertHreflang = (slugSelf, lang) => {
    // Remove existing hreflang tags we own
    document.querySelectorAll('link[data-managed-hreflang="1"]').forEach((n) => n.remove());
    const origin = window.location.origin;
    const make = (l, href) => {
        const lnk = document.createElement("link");
        lnk.setAttribute("rel", "alternate");
        lnk.setAttribute("hreflang", l);
        lnk.setAttribute("href", href);
        lnk.setAttribute("data-managed-hreflang", "1");
        document.head.appendChild(lnk);
    };
    make(lang, `${origin}/${slugSelf}`);
    make("x-default", `${origin}/${slugSelf}`);
};

export default function LocationPage({ slug }) {
    const page = LOCATION_PAGES[slug];

    useEffect(() => {
        if (!page) return;
        const prevTitle = document.title;
        const prevLang = document.documentElement.getAttribute("lang");
        const url = `${window.location.origin}/${slug}`;

        document.documentElement.setAttribute("lang", page.lang);
        document.title = page.metaTitle || page.title;
        ensureMeta("description").setAttribute("content", page.metaDescription || page.quickAnswer || "");
        ensureCanonical().setAttribute("href", url);
        upsertHreflang(slug, page.lang);

        upsertJsonLd("ld-article", {
            "@context": "https://schema.org",
            "@type": "Article",
            inLanguage: page.lang,
            headline: page.title,
            description: page.metaDescription || page.quickAnswer,
            author: { "@type": "Organization", name: "ADX Fusion" },
            publisher: { "@type": "Organization", name: "ADX Fusion", url: window.location.origin },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: page.country,
            url,
        });

        if (page.faqs && page.faqs.length > 0) {
            upsertJsonLd("ld-faq", {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                inLanguage: page.lang,
                mainEntity: page.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
            });
        }

        return () => {
            document.title = prevTitle;
            if (prevLang) document.documentElement.setAttribute("lang", prevLang);
            else document.documentElement.removeAttribute("lang");
            ["ld-article", "ld-faq"].forEach((id) => {
                const tag = document.getElementById(id);
                if (tag) tag.remove();
            });
            document.querySelectorAll('link[data-managed-hreflang="1"]').forEach((n) => n.remove());
        };
    }, [slug, page]);

    if (!page) {
        return (
            <div className="container-x py-32">
                <p className="text-brand-frost/80">Page not found.</p>
                <Link to="/" className="btn-secondary mt-6">
                    Back home
                </Link>
            </div>
        );
    }

    const isFr = page.lang === "fr";
    const eyebrow = isFr ? "Marche · " + page.country : "Market · " + page.country;
    const startProjectLabel = page.ctaButton || (isFr ? "Demarrer un projet" : "Start a project");
    const faqHeading = isFr ? "Questions frequentes" : "Frequently asked questions";

    return (
        <div data-testid={`location-page-${slug}`}>
            {/* HERO */}
            <section className="container-x pt-20 pb-12">
                <p className="overline mb-8">{eyebrow}</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.98] tracking-tight text-brand-mist max-w-[1200px] overflow-visible pr-2"
                        style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
                    >
                        {page.title}
                    </h1>
                </Reveal>
                <Reveal delay={120} className="mt-10 max-w-3xl">
                    <div className="border-l-2 border-brand-primary pl-6 py-2">
                        <p className="overline mb-3">{page.quickAnswerLabel}</p>
                        <p className="text-xl text-brand-frost leading-relaxed">{page.quickAnswer}</p>
                    </div>
                </Reveal>
            </section>

            {/* BODY */}
            <section className="container-x pb-20 border-t border-brand-primary/10 pt-4">
                <article className="max-w-3xl mx-auto">{renderMarkdown(page.body)}</article>
            </section>

            {/* FAQ */}
            {page.faqs && page.faqs.length > 0 ? (
                <section className="border-t border-brand-primary/10 py-20 bg-brand-surface/30">
                    <div className="container-x max-w-3xl mx-auto">
                        <p className="overline mb-6">{isFr ? "Questions frequentes" : "FAQ"}</p>
                        <h2 className="font-display h-display-md text-brand-mist mb-12">
                            {faqHeading}
                        </h2>
                        <div className="divide-y divide-brand-primary/10 border-y border-brand-primary/10">
                            {page.faqs.map((f, i) => (
                                <details key={f.question || `faq-${i}`} className="group py-6" data-testid={`location-faq-${slug}-${i}`}>
                                    <summary className="flex items-center justify-between cursor-pointer list-none">
                                        <h3 className="font-display text-lg lg:text-xl text-brand-mist pr-6">
                                            {f.question}
                                        </h3>
                                        <span className="shrink-0 w-6 h-6 border border-brand-primary/40 flex items-center justify-center text-brand-primary text-sm transition-transform group-open:rotate-45">
                                            +
                                        </span>
                                    </summary>
                                    <p className="mt-4 text-brand-frost/85 leading-relaxed">{f.answer}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* CTA */}
            <section className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
                    <Reveal>
                        <h2 className="font-display h-display-md max-w-3xl">
                            {isFr ? "Demarrer un projet" : "Start a project"}
                        </h2>
                        <p className="mt-6 max-w-2xl text-brand-frost/80 leading-relaxed">{page.ctaIntro}</p>
                    </Reveal>
                    <Reveal delay={120}>
                        <Link to="/contact" className="btn-primary">
                            {startProjectLabel} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
